import { useState } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { WidgetSize } from "../api/client";
import { DashboardDataProvider } from "./data";
import { WIDGETS, type WidgetDef } from "./registry";

/** A widget instance on the dashboard. `uid` is a session-local key for
 *  React/dnd-kit — it is NOT persisted (the saved layout is just the
 *  ordered list of {type, size, config}). */
export interface DashItem {
  uid: string;
  type: string;
  size: WidgetSize;
  config: Record<string, unknown>;
}

let uidCounter = 0;
export function newUid(): string {
  return `w${++uidCounter}`;
}

export default function Dashboard({
  items,
  editing,
  onChange,
}: {
  items: DashItem[];
  editing: boolean;
  onChange: (items: DashItem[]) => void;
}) {
  const sensors = useSensors(
    // Desktop: start after 6px so plain clicks still hit buttons.
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // Android: long-press to lift, so the page still scrolls normally.
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.uid === active.id);
    const to = items.findIndex((i) => i.uid === over.id);
    if (from < 0 || to < 0) return;
    onChange(arrayMove(items, from, to));
  }

  function addWidget(def: WidgetDef) {
    onChange([
      ...items,
      {
        uid: newUid(),
        type: def.type,
        size: def.defaultSize,
        config: { ...(def.defaultConfig ?? {}) },
      },
    ]);
    setPickerOpen(false);
  }

  const grid = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 items-start">
      {items.map((item) => (
        <DashboardCell
          key={item.uid}
          item={item}
          editing={editing}
          onRemove={() => onChange(items.filter((i) => i.uid !== item.uid))}
          onPatch={(patch) =>
            onChange(items.map((i) => (i.uid === item.uid ? { ...i, ...patch } : i)))
          }
        />
      ))}
      {editing && (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="md:col-span-2 rounded-xl border-2 border-dashed border-ink-700 p-5 text-sm text-ink-400 hover:border-brand-500 hover:text-brand-300 transition"
        >
          + Widget hinzufügen
        </button>
      )}
    </div>
  );

  return (
    <DashboardDataProvider>
      {editing ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((i) => i.uid)} strategy={rectSortingStrategy}>
            {grid}
          </SortableContext>
        </DndContext>
      ) : (
        grid
      )}
      {pickerOpen && (
        <WidgetPicker
          onPick={addWidget}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </DashboardDataProvider>
  );
}

function DashboardCell({
  item,
  editing,
  onRemove,
  onPatch,
}: {
  item: DashItem;
  editing: boolean;
  onRemove: () => void;
  onPatch: (patch: Partial<DashItem>) => void;
}) {
  const def = WIDGETS[item.type];
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.uid, disabled: !editing });

  // Unknown widget type (e.g. layout written by a newer app version):
  // silently skip in view mode, show a removable stub in edit mode.
  if (!def && !editing) return null;

  const span = item.size === "full" ? "md:col-span-2" : "";
  if (!editing) {
    const C = def!.Component;
    return (
      <div className={span}>
        <C config={item.config} size={item.size} />
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={
        span +
        " relative rounded-xl ring-1 ring-ink-700 " +
        (isDragging ? "z-10 opacity-80 ring-brand-500" : "")
      }
    >
      <div className="flex items-center gap-2 rounded-t-xl border-b border-ink-800 bg-ink-900/90 px-2 py-1.5">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing touch-none px-1.5 py-1 text-ink-400 hover:text-ink-100"
          aria-label="Widget verschieben"
          title="Ziehen zum Verschieben"
          {...attributes}
          {...listeners}
        >
          ⠿
        </button>
        <span className="text-xs font-medium text-ink-200 flex-1 truncate">
          {def?.label ?? `Unbekanntes Widget (${item.type})`}
        </span>
        {def?.EditControls && (
          <def.EditControls
            config={item.config}
            onConfig={(config) => onPatch({ config })}
          />
        )}
        {def?.configFields?.map((f) => (
          <label key={f.key} className="flex items-center gap-1 text-[11px] text-ink-400">
            {f.label}
            <select
              value={String(item.config[f.key] ?? "")}
              onChange={(e) => {
                const raw = e.target.value;
                const opt = f.options.find((o) => String(o.value) === raw);
                onPatch({ config: { ...item.config, [f.key]: opt?.value ?? raw } });
              }}
              className="bg-ink-800 border border-ink-700 rounded px-1 py-0.5 text-[11px] text-ink-200"
            >
              {f.options.map((o) => (
                <option key={String(o.value)} value={String(o.value)}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ))}
        <button
          type="button"
          onClick={() => onPatch({ size: item.size === "full" ? "half" : "full" })}
          className="hidden md:inline-block px-1.5 py-0.5 rounded text-[11px] border border-ink-700 text-ink-300 hover:border-brand-500 hover:text-brand-300"
          title={item.size === "full" ? "Auf halbe Breite verkleinern" : "Auf volle Breite vergrößern"}
        >
          {item.size === "full" ? "½ Breite" : "Volle Breite"}
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="px-1.5 py-1 text-ink-500 hover:text-bad-500"
          aria-label="Widget entfernen"
          title="Entfernen"
        >
          ✕
        </button>
      </div>
      {/* Inert while editing so links/buttons inside don't swallow taps. */}
      <div className="pointer-events-none select-none p-2 opacity-90">
        {def ? (
          <def.Component config={item.config} size={item.size} />
        ) : (
          <p className="text-sm text-ink-400 p-3">
            Dieses Widget kennt die App noch nicht — vermutlich stammt es aus
            einer neueren Version.
          </p>
        )}
      </div>
    </div>
  );
}

function WidgetPicker({
  onPick,
  onClose,
}: {
  onPick: (def: WidgetDef) => void;
  onClose: () => void;
}) {
  return createPortal(
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-ink-950/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md max-h-[80vh] overflow-y-auto rounded-xl border border-ink-700 bg-ink-900 shadow-cardHover"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="widget-picker-title"
      >
        <div className="px-5 pt-5 pb-2 flex items-baseline justify-between">
          <h2 id="widget-picker-title" className="text-base font-semibold text-ink-100">
            Widget hinzufügen
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-500 hover:text-ink-200 text-sm p-1"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>
        <ul className="px-3 pb-4 space-y-1.5">
          {Object.values(WIDGETS).map((def) => (
            <li key={def.type}>
              <button
                type="button"
                onClick={() => onPick(def)}
                className="w-full text-left rounded-lg border border-ink-800 bg-ink-950/40 p-3 hover:border-brand-400 transition"
              >
                <span className="block text-sm font-medium text-ink-100">{def.label}</span>
                <span className="block text-xs text-ink-400">{def.description}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
